import { useTranslation } from "react-i18next";
import { type FormValues } from "@/widgets/classic-form/form";
import { FieldComposer, FieldComposerFieldType } from "@/features/form-fields";
import { type ArrayFieldPath, type PrefixObjectAccessor } from "@/shared/forms";
import styles from "./Relatives.module.scss";

interface RelativeFieldGroupProps {
    title: string;
    givenNamePath: ArrayFieldPath<FormValues>;
    surnamePath: ArrayFieldPath<FormValues>;
    showExactness?: boolean;
}

const RelativeFieldGroup = ({ title, givenNamePath, surnamePath, showExactness = true }: RelativeFieldGroupProps) => {
    const { t } = useTranslation();

    return (
        <fieldset className={styles.fieldGroupWrapper}>
            <div>{title}</div>
            <div className={styles.fieldRow}>
                <div>
                    <FieldComposer
                        fieldPath={givenNamePath}
                        exactnessFieldPath={`${givenNamePath}Exactness.isExact` as PrefixObjectAccessor<FormValues, []>}
                        fieldConfig={{
                            fieldType: FieldComposerFieldType.String,
                            label: t("searchAssist.firstName"),
                            placeholder: t("searchAssist.firstName"),
                            showExactness,
                            required: false,
                        }}
                    >
                        <span className="noDisplay">
                            <FieldComposer.Label />
                        </span>
                        <FieldComposer.FieldConnector>
                            {({ formField, fieldConfig }, id) => (
                                <FieldComposer.Inputs.String
                                    id={id}
                                    placeholder={fieldConfig.placeholder}
                                    {...formField}
                                />
                            )}
                        </FieldComposer.FieldConnector>
                        <FieldComposer.GenericExactness />
                    </FieldComposer>
                </div>
                <div>
                    <FieldComposer
                        fieldPath={surnamePath}
                        exactnessFieldPath={`${surnamePath}Exactness.isExact` as PrefixObjectAccessor<FormValues, []>}
                        fieldConfig={{
                            fieldType: FieldComposerFieldType.String,
                            label: t("searchAssist.lastName"),
                            placeholder: t("searchAssist.lastName"),
                            showExactness,
                            required: false,
                        }}
                    >
                        <span className="noDisplay">
                            <FieldComposer.Label />
                        </span>
                        <FieldComposer.FieldConnector>
                            {({ formField, fieldConfig }, id) => (
                                <FieldComposer.Inputs.String
                                    id={id}
                                    placeholder={fieldConfig.placeholder}
                                    {...formField}
                                />
                            )}
                        </FieldComposer.FieldConnector>
                        <FieldComposer.GenericExactness />
                    </FieldComposer>
                </div>
            </div>
        </fieldset>
    );
};

export const Relatives = () => {
    const { t } = useTranslation();

    return (
        <div>
            <div>{t("searchAssist.familyMembersTitle")}</div>
            <div className={styles.pageTitle}>
                <span className="icon iconPeople"></span>
                <strong>{t("searchAssist.nameOfRelatives")}</strong>
                <span className={styles.optional}>{t("searchAssist.optional")}</span>
            </div>

            <RelativeFieldGroup
                title={t("searchAssist.child")}
                givenNamePath="family.child.instances.0.givenName"
                surnamePath="family.child.instances.0.surname"
                showExactness={false}
            />

            <RelativeFieldGroup
                title={t("searchAssist.spouse")}
                givenNamePath="family.spouse.instances.0.givenName"
                surnamePath="family.spouse.instances.0.surname"
            />

            <RelativeFieldGroup
                title={t("searchAssist.mother")}
                givenNamePath="family.mother.instances.0.givenName"
                surnamePath="family.mother.instances.0.surname"
            />

            <RelativeFieldGroup
                title={t("searchAssist.father")}
                givenNamePath="family.father.instances.0.givenName"
                surnamePath="family.father.instances.0.surname"
            />
        </div>
    );
};
